import { Injectable, Injector } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';

import { BreadCrumb } from '../models/common.models';
import { INavigationNodes, INavigationResponse, IStoreAddress } from '../models/settings.model';
import {
  BreadcrumbsService,
  FooterService,
  I18nService,
  NavigationBarService,
  ProductListService,
  ProductsService,
} from '../services';
import { State } from '../reducers';
import * as AppSelectors from '../reducers/app.reducer';
import * as AppActions from '../actions/app.actions';
import { ESeoData, ETitleSources } from '../configurations/seo';
import {
  EFeatureToggles,
  EFullPageLoaderListOptions,
  ERouteDataTypes,
  EStoreFeatures,
  FEATURE_TOGGLE,
} from '../configurations/common';
import { AppUtils } from '../utils/app.utils';
import { TermsUtils } from '../utils/terms.utils';
import { environment } from '../../environments/environment';
import { ISocialMediaFooter } from '../models/footers.models';
import { HomepageActions } from '../actions';
import {
  selectShopCategoryIsAnyTileActive,
  selectShopCategoryLoadingError,
  selectShopCategoryOptions,
  selectShopCategoryOptionsLoading,
} from '../reducers/homepage.reducer';
import { INotification } from '../models/cpq.models';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationFacade {
  private pNavigationBarService: NavigationBarService;
  private pFooterService: FooterService;
  private pBreadcrumbsService: BreadcrumbsService;
  private pProductService: ProductsService;
  private pProductListService: ProductListService;
  private pI18nService: I18nService;

  public get navigationBarService(): NavigationBarService {
    if (!this.pNavigationBarService) {
      this.pNavigationBarService = this.injector.get(NavigationBarService);
    }
    return this.pNavigationBarService;
  }

  public get productListService(): ProductListService {
    if (!this.pProductListService) {
      this.pProductListService = this.injector.get(ProductListService);
    }
    return this.pProductListService;
  }

  public get footerService(): FooterService {
    if (!this.pFooterService) {
      this.pFooterService = this.injector.get<FooterService>(FooterService);
    }
    return this.pFooterService;
  }

  public get breadcrumbsService(): BreadcrumbsService {
    if (!this.pBreadcrumbsService) {
      this.pBreadcrumbsService = this.injector.get<BreadcrumbsService>(BreadcrumbsService);
    }
    return this.pBreadcrumbsService;
  }

  public get productService(): ProductsService {
    if (!this.pProductService) {
      this.pProductService = this.injector.get(ProductsService);
    }
    return this.pProductService;
  }

  public get i18nService(): I18nService {
    if (!this.pI18nService) {
      this.pI18nService = this.injector.get(I18nService);
    }
    return this.pI18nService;
  }

  constructor(
    private injector: Injector,
    private store: Store<State>,
  ) {
  }

  getNavigationBar(isGuest: boolean): Observable<INavigationResponse> {
    return this.navigationBarService.getNavigationBar(isGuest);
  }

  getShopCategoryTiles(): Observable<INavigationResponse> {
    return this.navigationBarService.getShopCategoryNavigationBar();
  }

  loadShopCategoryTiles() {
    this.store.dispatch(HomepageActions.loadShopCategoryNavigation());
  }

  selectShopCategoryTiles(): Observable<INavigationNodes[]> {
    return this.store.select(selectShopCategoryOptions);
  }

  selectShopCategoryTilesLoading(): Observable<boolean> {
    return this.store.select(selectShopCategoryOptionsLoading);
  }

  selectShopCategoryTilesError(): Observable<string> {
    return this.store.select(selectShopCategoryLoadingError);
  }

  selectShopCategoryIsAnyTileActive(): Observable<boolean> {
    return this.store.select(selectShopCategoryIsAnyTileActive);
  }

  getFooterNavigation(): Observable<any> {
    return this.footerService.getFooterNav();
  }

  getSocialMediaFooter(): Observable<ISocialMediaFooter> {
    return this.footerService.getSocialMediaFooter();
  }

  getBreadcrumbs(route: ActivatedRoute): BreadCrumb[] {
    return this.breadcrumbsService.getBreadcrumbs(route);
  }

  getBreadcrumbsParam(route: ActivatedRoute, param: ESeoData | ERouteDataTypes): ETitleSources | boolean {
    return this.breadcrumbsService.getBreadcrumbsParam(route, param);
  }

  getProductPhotos(id: string): Observable<any> {
    return this.productService.getProductPhotos(id);
  }

  getProductPrices(id: string): Observable<any> {
    return this.productService.getProductPrices(id);
  }

  getProductWithCategoryTree(sku: string): Promise<any> {
    return this.productService.getProductWithCategoryTree(sku);
  }

  getAbstractProductWithInstalledBase(sku: string): Observable<any> {
    return this.productService.getAbstractProductWithInstalledBase(sku);
  }

  getDefaultLanguage(): string {
    return this.i18nService.getDefaultLanguage();
  }

  validateMarket(market: string): boolean {
    return this.i18nService.validateMarket(market);
  }

  validateLanguage(language: string): boolean {
    return this.i18nService.validateLanguage(language);
  }

  validateLangParam(param: Params): boolean {
    return this.i18nService.validateLangParam(param);
  }

  getLangParamData(langParam: string): string[] {
    return this.i18nService.getLangParamData(langParam);
  }

  buildLangParam(market: string, language: string): string {
    return this.i18nService.buildLangParam(market, language);
  }

  getCurrentLanguage(): string {
    return this.i18nService.getCurrentLanguage();
  }

  getCurrentParam(): string {
    return this.i18nService.getCurrentParam();
  }

  getLanguageRegex(url = false): RegExp {
    if (url) {
      return I18nService.languageUrlRegex;
    }
    return I18nService.languageParamRegex;
  }

  getTranslationByKey(key: string | Array<string>, params?: any): Observable<any> {
    return this.i18nService.getTranslationByKey(key, params);
  }

  getCurrentStoreAddress(): IStoreAddress {
    let localizedAddress = AppUtils.getCurrentStore()?.address[this.getCurrentLanguage()];
    return localizedAddress ? localizedAddress : AppUtils.getCurrentStore()?.address['default'];
  }

  setAlert(alert: any): void {
    this.store.dispatch(AppActions.setAlert({alert}));
  }

  clearAlert(): void {
    this.store.dispatch(AppActions.clearAlert());
  }

  getAlert(): Observable<any> {
    return this.store.select(AppSelectors.getAlert);
  }

  getTermsUrl(termsKey: string, lang?: string): any {
    if (!lang) {
      lang = this.getCurrentLanguage();
    }
    const store = AppUtils.getCurrentStore();
    return TermsUtils.getTermsUrl(termsKey, lang, store);
  }

  /**
   * Return true if store feature is available for current store within the built environment,
   * otherwise returns false.
   *
   * @param {EStoreFeatures} feature
   * @returns {boolean}
   */
  isFeatureAvailable(feature: EStoreFeatures): boolean {
    const currentStoreId = AppUtils.getCurrentStore().marketCode;
    const features = environment.features;
    return features[feature].includes(currentStoreId.toUpperCase());
  }

  /**
   * Return true if store feature is enabled via toggle,
   * otherwise return false
   *
   * @param {EFeatureToggles} featureToggle
   * @returns {Observable<boolean>}
   */
  isFeatureEnabled(featureToggle: EFeatureToggles): Observable<boolean> {
    const featureToggleKey = FEATURE_TOGGLE + featureToggle;
    return this.i18nService.getTranslationByKey(featureToggleKey).pipe(
      map(translationValue => translationValue.toString().toLowerCase() === 'true'),
    );
  }

  /**
   * setTimout function is used, because delay is needed so the notification is
   * not closed with the same click, which opened it
   *
   * setTimeout is used as a "zero-delay timeout/deferred function" technique
   *
   * @param {INotification} notification
   */
  appendNotification(notification: INotification): void {
    setTimeout(() => {
      this.store.dispatch(AppActions.appendNotification({notification}));
    });
  }

  closeNotification(notificationId: number) {
    this.store.dispatch(AppActions.closeNotification({notificationId}));
  }

  clearNotifications(): void {
    this.store.dispatch(AppActions.clearNotifications());
  }

  getNotification(): Observable<any> {
    return this.store.select(AppSelectors.getNotification);
  }

  redirectToLastPage(): void {
    this.store.dispatch(AppActions.redirectToLastPage());
  }

  selectFullPageLoaderList(): Observable<any> {
    return this.store.select(AppSelectors.selectFullPageLoaderList);
  }

  navBarLoaded(): void {
    this.store.dispatch(AppActions.navBarLoaded({name: EFullPageLoaderListOptions.NAVIGATION}));
  }

  homePageBannerLoaded(): void {
    this.store.dispatch(AppActions.homePageBannerLoaded({name: EFullPageLoaderListOptions.HOMEPAGE_BANNER}));
  }
}
